import React, { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { InView } from 'react-intersection-observer';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { BiData, SetMultiImageDisplayModeClickBiEvent } from '@wix/da-bi/pkg/events';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import CarouselIcon from '@wix/da-ds/pkg/Icons/24x24/Carousel';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import noop from '../../utils/noop';
import { useDeviationViewContext } from '../../Context';
import DuperbrowsePaddles from '../../Duperbrowse/DuperbrowsePaddles';
import EmptyBackground from '../../DeviationBackground/StageBackground/EmptyBackground';
import { SubscriptionPurchaseFTUEContext } from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import SubscriptionPurchaseFTUE from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUE';
import {
  ADDITIONAL_MEDIA_ID_PREFIX,
  BI_SCROLL_SECTIONNAME,
  SCROLL_MODE_THRESHOLD_FOR_INVIEW,
} from '../constants';
import { useMeasuredStage } from '../hooks/useMeasuredStage';
import { useScrollToCurrentlyViewed } from '../hooks/useScrollToCurrentlyViewed';
import StageContainer from '../_partials/StageContainer';
import ZoomModalFromImageSource from '../_partials/ZoomModalFromImageSource';
import StashPaddles from '../_partials/StashPaddles';
import FullImageSourceWithCaption from '../_partials/FullImageSourceWithCaption';
import { DefaultArtStageProps as Props } from '../types';
import { useSelector } from 'react-redux';
import { getIsDuperbrowseActive } from '../../Duperbrowse/redux/selectors';

import styles from './DesktopArtStage.scss';

/** Multi-image deviation that displays as a vertical list of images one above the other */
export const DesktopMultiArtScrollStage: React.FC<Props> = ({
  deviation,
  additionalMedia = [],
  className,
  currentlyViewedImageSource,
  setCurrentlyViewedImageSource = noop,
  zoomedImageSource,
  isZoomed,
  onEnterZoomMode = noop,
  onExitZoomMode = noop,
  setDisplayMode = noop,
}) => {
  const { t } = useTranslation();
  const isSubscriptionPurchaseFTUEBeingShown = !!useContext(SubscriptionPurchaseFTUEContext);
  const isDuperbrowseActive = useSelector(getIsDuperbrowseActive);

  const { isFullscreen } = useDeviationViewContext();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const { contentSize = {} } = useMeasuredStage(
    viewportRef,
    !isFullscreen,
    MeasuredCookieType.DEVIATION_MULTI_IMAGE_SCROLL
  );

  // if there's a currentlyViewedImageSource, scroll to it
  const isScrolling = useScrollToCurrentlyViewed(
    deviation,
    additionalMedia,
    currentlyViewedImageSource
  );

  return (
    <StageContainer
      className={classnames(
        className,
        styles['root'],
        styles['scroll-mode'],
        isFullscreen && styles['fullscreen']
      )}
      withoutInnerWrapper
    >
      <BiLoggerContextProvider value={BI_SCROLL_SECTIONNAME}>
        {/* since the stage must be taller than the viewport,
          use a separate, height-constrained stand-in to get displayable/content size */}
        <div className={styles['viewport-measurer']}>
          <div ref={viewportRef} />

          <IconButton
            className={styles['switch-to-carousel-button']}
            icon={CarouselIcon}
            size="large"
            tooltipText={t('deviationview.artstage.changeToCarouselMode.tooltip')}
            tooltipOffset={[0, 14]}
            aria-label={t('deviationview.artstage.changeToCarouselMode.altLabel')}
            onClick={() => {
              setDisplayMode('carousel');
              window.scrollTo(0, 0);
            }}
            biData={BiData<SetMultiImageDisplayModeClickBiEvent>({
              evid: 309,
              deviationid: deviation.deviationId,
              view_mode: 'carousel',
            })}
          />

          {(deviation.isPublished || isDuperbrowseActive) && (
            <DuperbrowsePaddles className={styles['duperbrowse']} isInZoomModal={isZoomed} />
          )}
          {!deviation.isPublished && !isDuperbrowseActive && <StashPaddles />}
        </div>

        <div className={styles['content']}>
          <EmptyBackground />

          <ol
            className={classnames(
              styles['scroll-mode-list'],
              isScrolling && styles['scroll-mode-list-scrolling']
            )}
          >
            <InView
              key="original-deviation"
              as="li"
              threshold={SCROLL_MODE_THRESHOLD_FOR_INVIEW}
              onChange={inView => {
                inView && !isScrolling && setCurrentlyViewedImageSource(deviation);
              }}
            >
              <FullImageSourceWithCaption
                id="deviation-image"
                deviationOrAttachment={deviation}
                withoutZoomModal
                {...contentSize}
                isZoomed={isZoomed}
                onEnterZoomMode={onEnterZoomMode}
                onExitZoomMode={onExitZoomMode}
              />
            </InView>

            {additionalMedia.map((image, i) => {
              return (
                <InView
                  key={`additional-media-${image.filename}`}
                  as="li"
                  threshold={SCROLL_MODE_THRESHOLD_FOR_INVIEW}
                  onChange={inView => {
                    inView && !isScrolling && setCurrentlyViewedImageSource(image);
                  }}
                >
                  <FullImageSourceWithCaption
                    id={`${ADDITIONAL_MEDIA_ID_PREFIX}${i}`}
                    deviationOrAttachment={image}
                    dontExpandWrapper
                    withoutZoomModal
                    {...contentSize}
                    isZoomed={isZoomed}
                    onEnterZoomMode={onEnterZoomMode}
                    onExitZoomMode={onExitZoomMode}
                  />
                </InView>
              );
            })}
          </ol>
        </div>

        {zoomedImageSource && (
          <ZoomModalFromImageSource
            source={zoomedImageSource}
            allowFullviewSrcsetForPngs={(zoomedImageSource as PapiDeviation)?.isPublished ?? false}
            {...contentSize}
            isZoomed={isZoomed}
            onExitZoomMode={onExitZoomMode}
          />
        )}

        {isSubscriptionPurchaseFTUEBeingShown && (
          <SubscriptionPurchaseFTUE className={styles['subscription-purchase-ftue']} />
        )}
      </BiLoggerContextProvider>
    </StageContainer>
  );
};
DesktopMultiArtScrollStage.displayName = 'DesktopMultiArtScrollStage';
export default DesktopMultiArtScrollStage;
